import {
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  DestructiveConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
  Input,
  Tabs,
  TabsList,
  TabsTrigger,
  useStateWithSetAction,
  VirtualizedDataTable,
  VirtualizedDataTableProps,
  withKey,
} from "@tellescope/ui";
import { useTicketTable, UseTicketTableArgs } from "../../hooks/useTicketTable";
import { useTranslation } from "react-i18next";
import { Plus, Table, Trash2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { TellescopeModelFromName, useTellescopeMutation } from "@tellescope/tanstack-query";
import { useAppPermissionQuery } from "../../permissions";
import { TELLESCOPE_2_TABLE_VIEW_PREFIX } from "../../hooks";
import { useForm } from "@tellescope/forms";
import { useMemo, useState } from "react";
import { AddTicketSheet } from "../AddTicketSheet";
import { DEFAULT_TICKETS_VIEW } from "../../store";
import { TicketTableConfigDialog } from "./TicketTableConfigDialog";
import { ConfigureTableViewButton } from "../tables/ConfigureTableViewButton";

export interface TicketsTableProps extends UseTicketTableArgs {
  maxTabCount?: number;
  height?: string;
  minHeight?: string;
  hideViewsTabs?: boolean;
  hideSaveAsView?: boolean;
}

export const TicketsTable = withKey(
  ({
    maxTabCount = 3,
    height = "calc(100vh - 340px)",
    minHeight = "400px",
    hideViewsTabs = false,
    hideSaveAsView = false,
    ...tableConfigProps
  }: TicketsTableProps) => {
    const { t } = useTranslation("tickets");
    const navigate = useNavigate();
    const ticketTable = useTicketTable(tableConfigProps);
    const [isAddViewDialogOpen, setIsAddViewDialogOpen] = useStateWithSetAction(false, (isOpen) => {
      if (!isOpen) {
        addViewForm.resetForm();
        setIsSaveAsView(false);
      }
    });
    const [isSaveAsView, setIsSaveAsView] = useState(false);
    const [viewConfigDialogIsOpen, setViewConfigDialogIsOpen] = useState(false);
    const [isAddTicketSheetOpen, setIsAddTicketSheetOpen] = useState(false);
    const [isOtherViewsDialogOpen, setIsOtherViewsDialogOpen] = useState(false);

    const addViewForm = useForm({
      fields: {
        title: {
          type: "text",
          required: true,
          initialValue: "",
        },
      },
      validateOn: "blur",
      onSubmit: (values) => {
        if (!values.title) return;

        const saveAsFilter = isSaveAsView
          ? {
              ...ticketTable.selectedView.filter,
              ...(ticketTable.isDefaultView ? { ownerIds: [ticketTable.currentUserId] } : undefined),
            }
          : undefined;

        createTableViewMutation.mutateAsync(
          {
            page: `${TELLESCOPE_2_TABLE_VIEW_PREFIX}Tickets`,
            title: values.title,
            columns: isSaveAsView ? ticketTable.selectedView.columns : DEFAULT_TICKETS_VIEW.columns,
            ...(saveAsFilter !== undefined ? { filter: saveAsFilter } : undefined),
          },
          {
            onSuccess: (res) => {
              if (isSaveAsView) {
                ticketTable.resetDefaultView();
              }
              navigate(`/tickets/views/${res.id}`);
            },
          }
        );
        setIsAddViewDialogOpen(false);
      },
    });

    const createTableViewMutation = useTellescopeMutation({
      model: "table_views",
      method: "createOne",
    });

    const deleteTableViewMutation = useTellescopeMutation({
      model: "table_views",
      method: "deleteOne",
    });
    const {
      delete: { access: canDelete },
    } = useAppPermissionQuery({
      model: "table_views",
      actions: ["delete"],
    });

    const { viewsToDisplay, rolledUpViews } = ticketTable.views.reduce<{
      viewsToDisplay: TellescopeModelFromName<"table_views">[];
      rolledUpViews: TellescopeModelFromName<"table_views">[];
    }>(
      (acc, curr) => {
        if (curr.id === tableConfigProps.viewId) {
          if (acc.viewsToDisplay.length === maxTabCount - 1) {
            const viewToMove = acc.viewsToDisplay.pop();
            if (viewToMove) acc.rolledUpViews.push(viewToMove);
            acc.viewsToDisplay.push(curr);
            return acc;
          }

          acc.viewsToDisplay.push(curr);
          return acc;
        }

        if (acc.viewsToDisplay.length < maxTabCount - 1) {
          acc.viewsToDisplay.push(curr);
        } else {
          acc.rolledUpViews.push(curr);
        }

        return acc;
      },
      { viewsToDisplay: [], rolledUpViews: [] }
    );

    const activeFiltersCount = ticketTable.activeFiltersCount;
    const [deleteDialogIsOpen, setDeleteDialogIsOpen] = useState(false);
    const actionButtons = useMemo<
      VirtualizedDataTableProps<TellescopeModelFromName<"tickets">>["actionButtons"]
    >(() => {
      const result: VirtualizedDataTableProps<TellescopeModelFromName<"tickets">>["actionButtons"] =
        ticketTable.viewId === "default" || !canDelete
          ? []
          : [
              {
                icon: Trash2,
                label: "",
                variant: "destructive",
                hoverLabel: t("Delete View"),
                onClick: () => setDeleteDialogIsOpen(true),
              },
            ];

      if (!hideSaveAsView) {
        const hasSorting =
          (!activeFiltersCount && !!ticketTable.isDefaultViewModified) ||
          !!ticketTable.virtualizedTableProps.sorting.length;
        result.push(
          <ConfigureTableViewButton
            hasFilter={!!activeFiltersCount}
            filterCount={activeFiltersCount}
            hasSorting={hasSorting}
            onClick={() => setViewConfigDialogIsOpen(true)}
            hoverLabel={t("Configure View")}
            data-trackingid="tickets-table-configure-view"
          />
        );
      }
      if (ticketTable.isDefaultViewModified) {
        if (!hideSaveAsView) {
          result.push({
            label: t("Save as View"),
            onClick: () => {
              setIsSaveAsView(true);
              setIsAddViewDialogOpen(true);
            },
            trackingId: "tickets-table-save-default-as-view",
            variant: "outline",
          });
        }
      }

      result.push({
        icon: Plus,
        label: t("Add Ticket"),
        onClick: () => {
          setIsAddTicketSheetOpen(true);
        },
        trackingId: "tickets-table-add-ticket",
      });
      return result;
    }, [
      activeFiltersCount,
      canDelete,
      hideSaveAsView,
      setIsAddViewDialogOpen,
      t,
      ticketTable.isDefaultViewModified,
      ticketTable.viewId,
      ticketTable.virtualizedTableProps.sorting.length,
    ]);

    return (
      <div className="w-full">
        {!hideViewsTabs && (
          <div className="flex gap-2 items-center mb-4">
            <Tabs value={ticketTable.viewId} data-trackingid="tickets-table-view-selector-tabs">
              <TabsList>
                <TabsTrigger value="default" asChild>
                  <Link to="/tickets/views/default">
                    <Table className="w-4 h-4" />
                    {t("Default")}
                  </Link>
                </TabsTrigger>
                {viewsToDisplay.map((view) => (
                  <TabsTrigger key={view.id} value={view.id} asChild>
                    <Link to={`/tickets/views/${view.id}`}>
                      <Table className="w-4 h-4" />
                      {view.title}
                    </Link>
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            {!!rolledUpViews.length && (
              <Dialog open={isOtherViewsDialogOpen} onOpenChange={setIsOtherViewsDialogOpen}>
                <DialogTrigger asChild>
                  <button
                    className="p-0 border-0 bg-transparent cursor-pointer text-muted-foreground text-sm"
                    onClick={() => {
                      setIsOtherViewsDialogOpen(true);
                    }}
                  >
                    {t("and {{amount}} more...", { amount: rolledUpViews.length })}
                  </button>
                </DialogTrigger>
                <DialogContent className="p-0 border-0 bg-transparent shadow-none [&>[data-slot=dialog-close]]:top-3 [&>[data-slot=dialog-close]]:right-3 [&>[data-slot=dialog-close]]:focus:ring-0 [&>[data-slot=dialog-close]]:focus:ring-offset-0">
                  <Command className="rounded-lg border max-w-lg bg-popover">
                    <CommandInput placeholder="Type a command or search" className="h-12 p-3" />
                    <CommandList>
                      <CommandEmpty>{t("No results found.")}</CommandEmpty>
                      {rolledUpViews.map((view) => (
                        <CommandItem key={view.id} value={view.id} asChild className="px-4 py-3 cursor-pointer">
                          <Link to={`/tickets/views/${view.id}`}>
                            <Table />
                            {view.title}
                          </Link>
                        </CommandItem>
                      ))}
                    </CommandList>
                  </Command>
                </DialogContent>
              </Dialog>
            )}
            <Dialog open={isAddViewDialogOpen} onOpenChange={setIsAddViewDialogOpen}>
              <DialogTrigger asChild>
                <button
                  className="p-0 border-0 bg-transparent cursor-pointer outline-none focus:outline-none"
                  data-trackingid="tickets-table-create-table-view-dialog-open"
                >
                  <Plus className="w-6 h-6 text-muted-foreground hover:text-foreground transition-colors" />
                </button>
              </DialogTrigger>
              <DialogContent>
                <div className="space-y-1">
                  <DialogTitle className="font-medium text-sm">
                    {isSaveAsView ? t("Save as View") : t("Create New Table")}
                  </DialogTitle>
                  <DialogDescription className="text-xs">
                    {isSaveAsView
                      ? t("Enter a name for the new view. It will be saved with your current column configuration.")
                      : t("Enter a name for your new table view. Press Enter to add it to available views.")}
                  </DialogDescription>
                </div>
                <form {...addViewForm.getFormProps()} className="flex flex-col gap-2">
                  <Input
                    placeholder={t("View name...")}
                    {...addViewForm.getFieldProps("title")}
                    onKeyDownCapture={(e) => {
                      if (e.key === "Enter" && addViewForm.values.title) {
                        e.stopPropagation();
                        addViewForm.submitForm();
                      }
                    }}
                    autoFocus
                  />
                  <div className="flex justify-end gap-2">
                    <Button
                      onClick={() => {
                        setIsAddViewDialogOpen(false);
                      }}
                      variant="outline"
                    >
                      {t("Cancel")}
                    </Button>
                    <Button
                      type="submit"
                      disabled={!addViewForm.isValid}
                      data-trackingid="tickets-table-create-table-view-dialog-confirm"
                    >
                      {isSaveAsView ? t("Save View") : t("Add View")}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}

        <VirtualizedDataTable
          {...ticketTable.virtualizedTableProps}
          className={`min-h-[${minHeight}]`}
          height={height}
          enableSelection
          actionButtons={actionButtons}
          enableSorting={!tableConfigProps.queueId}
        />

        <AddTicketSheet
          open={isAddTicketSheetOpen}
          onOpenChange={setIsAddTicketSheetOpen}
          onTicketCreate={() => setIsAddTicketSheetOpen(false)}
          queueId={tableConfigProps.queueId}
        />
        {!ticketTable.isLoadingViews && (
          <TicketTableConfigDialog
            title={t("Configure {{tableViewName}}", { tableViewName: ticketTable.selectedView.title || t("Default") })}
            open={viewConfigDialogIsOpen}
            onOpenChange={(open) => setViewConfigDialogIsOpen(open)}
            onClearAll={
              ticketTable.isDefaultViewModified
                ? () => {
                    ticketTable.resetDefaultView();
                    setViewConfigDialogIsOpen(false);
                  }
                : undefined
            }
            columnConfigFormProps={{
              combinedFieldToColumnConfigMap: ticketTable.combinedFieldToColumnConfigMap,
              columnVisibility: ticketTable.columnVisibility,
              orderedVisibleColumnIds: ticketTable.orderedVisibleColumnIds,
            }}
            onApplyConfig={ticketTable.onApplyConfig}
            activeFilters={ticketTable.activeFilters}
            isDefaultView={ticketTable.isDefaultView}
            currentUserId={ticketTable.currentUserId}
            isDirty={ticketTable.isDefaultViewModified}
          />
        )}
        <DestructiveConfirmDialog
          onConfirm={() => {
            deleteTableViewMutation.mutateAsync(ticketTable.viewId);
            navigate("/tickets/default");
          }}
          loading={deleteTableViewMutation.isPending}
          open={deleteDialogIsOpen}
          onOpenChange={(open) => setDeleteDialogIsOpen(open)}
          description={t("This will permanently delete the view. This action cannot be undone.")}
        />
      </div>
    );
  },
  "viewId"
);
